import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Clock, Zap, ArrowLeft, Search, Star, MessageSquare, Send, Loader2, BarChart3 } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { browseCapsules, buyPass, previewPay, getCapsuleReviews, createCapsuleReview } from "../lib/api";
import { toast } from "sonner";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";

const CATEGORY_COLORS = {
  general: "#94A3B8", research: "#3B82F6", coding: "#10B981", finance: "#F5DF4D", support: "#A78BFA",
};
const CATEGORIES = ["all", "general", "research", "coding", "finance", "support"];

function RatingStars({ rating, size = 12, interactive = false, onRate }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={size}
          onClick={interactive ? () => onRate(i) : undefined}
          className={`${i <= Math.round(rating) ? "text-[#E5FF00] fill-[#E5FF00]" : "text-[#273142]"} ${interactive ? "cursor-pointer hover:text-[#E5FF00]" : ""}`}
        />
      ))}
    </div>
  );
}

function ReviewSection({ capsuleId, user }) {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCapsuleReviews(capsuleId).then(res => setReviews(res.data)).catch(() => {});
  }, [capsuleId]);

  const handleSubmit = async () => {
    if (!rating) { toast.error("Select a rating"); return; }
    if (!user) { toast.error("Sign in to review"); return; }
    setSubmitting(true);
    try {
      await createCapsuleReview(capsuleId, { rating, comment: comment.trim() });
      toast.success("Review submitted");
      setShowForm(false);
      setRating(0);
      setComment("");
      const res = await getCapsuleReviews(capsuleId);
      setReviews(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally { setSubmitting(false); }
  };

  const hasReviewed = reviews.some(r => r.user_id === user?.user_id);

  return (
    <div className="mt-3 pt-3 border-t border-[#273142] animate-fade-in">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] text-[#475569] uppercase tracking-wider flex items-center gap-1">
          <MessageSquare size={10} /> {reviews.length} Review{reviews.length !== 1 ? "s" : ""}
        </span>
        {user && !hasReviewed && !showForm && (
          <button data-testid={`write-review-${capsuleId}`} onClick={() => setShowForm(true)}
            className="font-mono text-[10px] text-[#E5FF00] hover:underline">Write Review</button>
        )}
      </div>

      {showForm && (
        <div data-testid={`review-form-${capsuleId}`} className="bg-[#080B10] border border-[#273142] rounded-lg p-3 mb-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-[#475569]">Rating:</span>
            <RatingStars rating={rating} size={16} interactive onRate={setRating} />
            {rating > 0 && <span className="font-mono text-[10px] text-[#E5FF00]">{rating}/5</span>}
          </div>
          <Textarea data-testid={`review-comment-${capsuleId}`} value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Share your experience..." rows={2}
            className="bg-[#111620] border-[#273142] text-white text-xs rounded-lg resize-none focus:border-[#E5FF00]" />
          <div className="flex gap-2">
            <button data-testid={`submit-review-${capsuleId}`} onClick={handleSubmit} disabled={submitting || !rating}
              className="bg-[#E5FF00] text-[#080B10] font-mono text-[10px] px-3 py-1.5 rounded-lg hover:bg-[#D4EC00] disabled:opacity-40 flex items-center gap-1">
              {submitting ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Submit
            </button>
            <button onClick={() => { setShowForm(false); setRating(0); setComment(""); }}
              className="font-mono text-[10px] text-[#475569] hover:text-white px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {reviews.slice(0, 5).map(r => (
            <div key={r.id} className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5"><RatingStars rating={r.rating} size={8} /></div>
              <div className="flex-1 min-w-0">
                {r.comment && <p className="text-[11px] text-[#94A3B8] line-clamp-2">{r.comment}</p>}
                <span className="font-mono text-[10px] text-[#475569]">{r.user_name} &middot; {r.created_at.slice(0, 10)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MarketplacePage({ user }) {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const [capsules, setCapsules] = useState([]);
  const [selectedCapsule, setSelectedCapsule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => { loadCapsules(); }, []);

  const loadCapsules = async () => {
    try { setCapsules((await browseCapsules()).data); } catch (e) { console.error("Failed to load capsules:", e); }
  };

  const handleBuyPass = async (capsule) => {
    if (!user) { toast.error("Sign in to purchase"); return; }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await buyPass(capsule.id, origin);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Purchase failed"); }
    finally { setLoading(false); }
  };

  const handlePreviewPay = async (capsule) => {
    if (!user) { toast.error("Sign in to preview"); return; }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await previewPay(capsule.id, origin);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    finally { setLoading(false); }
  };

  const filtered = capsules.filter((cap) => {
    const matchesSearch = !searchQuery || cap.title.toLowerCase().includes(searchQuery.toLowerCase()) || (cap.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || cap.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div data-testid="marketplace-page" className="h-screen bg-[#080B10] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#273142] px-8 py-4 flex items-center gap-4">
        <button data-testid="marketplace-back-btn" onClick={() => navigate("/agents")}
          className="text-[#475569] hover:text-[#E5FF00] transition-colors flex items-center gap-1.5">
          <ArrowLeft size={16} /><span className="font-mono text-xs uppercase tracking-wider">Back</span>
        </button>
        <div className="flex items-center gap-2 ml-2">
          <div className="w-2 h-2 bg-[#E5FF00]" />
          <h1 className="text-lg font-semibold tracking-tight">MARKETPLACE</h1>
        </div>
        <p className="text-xs text-[#475569] font-mono ml-2 hidden sm:block"></p>
        <div className="ml-auto flex items-center gap-3">
          <button data-testid="nav-earnings" onClick={() => navigate("/earnings")}
            className="flex items-center gap-1.5 text-[#94A3B8] hover:text-[#E5FF00] transition-colors text-xs font-mono">
            <BarChart3 size={14} /> Earnings
          </button>
          <SignedOut><SignInButton mode="modal"><button data-testid="marketplace-sign-in" className="bg-[#E5FF00] text-[#080B10] font-semibold uppercase tracking-wider text-[10px] px-4 py-2 hover:bg-[#D4EC00] transition-colors rounded-lg">SIGN IN</button></SignInButton></SignedOut>
          <SignedIn><div className="flex items-center gap-2"><span className="text-xs text-[#94A3B8]">{clerkUser?.firstName}</span><UserButton appearance={{ elements: { userButtonAvatarBox: "w-7 h-7" } }} /></div></SignedIn>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-[#273142] px-8 py-3 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <Input data-testid="marketplace-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search capsules..." className="bg-[#111620] border-[#273142] text-white rounded-lg pl-9 focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00] text-sm h-9" />
        </div>
        <div className="flex items-center gap-1">
          {CATEGORIES.map((cat) => (
            <button key={cat} data-testid={`filter-${cat}`} onClick={() => setActiveCategory(cat)}
              className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border rounded-full transition-colors ${
                activeCategory === cat ? "bg-[#E5FF00]/10 text-[#E5FF00] border-[#E5FF00]/40" : "text-[#475569] border-[#273142] hover:border-[#475569] hover:text-[#94A3B8]"
              }`}>{cat}</button>
          ))}
        </div>
        <span className="font-mono text-[10px] text-[#475569] ml-auto">{filtered.length} capsule{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Capsule Grid */}
      <ScrollArea className="flex-1">
        <div className="px-8 py-6">
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <ShoppingBag size={32} className="text-[#273142] mx-auto mb-3" />
              <p className="text-[#475569] text-sm font-mono uppercase tracking-widest">No capsules found</p>
              <p className="text-[#475569] text-xs mt-2">
                {capsules.length === 0 ? "Publish your first chat from the Inspector panel" : "Try a different search or category"}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((cap) => (
              <div key={cap.id} data-testid={`capsule-${cap.id}`}
                className={`bg-[#111620] border p-5 transition-all cursor-pointer flex flex-col rounded-xl ${
                  selectedCapsule?.id === cap.id ? "border-[#E5FF00]/60 shadow-[0_0_20px_rgba(229,255,0,0.08)]" : "border-[#273142] hover:border-[#E5FF00]/30"
                }`}
                onClick={() => setSelectedCapsule(selectedCapsule?.id === cap.id ? null : cap)}>
                {/* Card header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold truncate mb-1">{cap.title}</h3>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] px-1.5 py-0.5 border uppercase rounded-full"
                        style={{ color: CATEGORY_COLORS[cap.category] || "#94A3B8", borderColor: (CATEGORY_COLORS[cap.category] || "#94A3B8") + "40" }}>
                        {cap.category}
                      </span>
                      {(cap.avg_rating || 0) > 0 && (
                        <div className="flex items-center gap-1">
                          <RatingStars rating={cap.avg_rating} size={10} />
                          <span className="font-mono text-[10px] text-[#94A3B8]">{cap.avg_rating}</span>
                          <span className="font-mono text-[10px] text-[#475569]">({cap.review_count})</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs text-[#94A3B8] line-clamp-2 mb-3 flex-1">{cap.description || "No description provided"}</p>

                {/* Meta */}
                <div className="flex items-center gap-3 mb-3 text-[#475569]">
                  {cap.provider_type && <span className="font-mono text-[10px] uppercase bg-[#1C2433] border border-[#273142] px-1.5 py-0.5 rounded">{cap.provider_type}</span>}
                  {cap.model_name && <span className="font-mono text-[10px] bg-[#1C2433] border border-[#273142] px-1.5 py-0.5 rounded">{cap.model_name}</span>}
                  <span className="font-mono text-[10px] ml-auto">{cap.usage_count} uses</span>
                </div>

                {/* Pricing */}
                <div className="flex items-center justify-between border-t border-[#273142] pt-3">
                  <div className="flex items-center gap-1">
                    <Zap size={11} className="text-[#E5FF00]" />
                    <span className="font-mono text-xs text-[#E5FF00]">${cap.preview_price.toFixed(2)}</span>
                    <span className="font-mono text-[10px] text-[#475569]">/query</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={11} className="text-[#3B82F6]" />
                    <span className="font-mono text-xs text-[#3B82F6]">${cap.pass_price.toFixed(2)}</span>
                    <span className="font-mono text-[10px] text-[#475569]">/{cap.duration_days}d</span>
                  </div>
                </div>

                {/* Expanded: actions + reviews */}
                {selectedCapsule?.id === cap.id && (
                  <>
                    <div className="mt-3 pt-3 border-t border-[#273142] flex gap-2 animate-fade-in">
                      <button data-testid={`preview-pay-${cap.id}`}
                        onClick={(e) => { e.stopPropagation(); handlePreviewPay(cap); }} disabled={loading}
                        className="flex-1 bg-transparent text-[#E5FF00] border border-[#E5FF00]/40 hover:bg-[#E5FF00]/10 font-mono text-[10px] uppercase tracking-wider px-3 py-2 transition-colors flex items-center justify-center gap-1 disabled:opacity-50 rounded-lg">
                        <Zap size={10} /> Preview ${cap.preview_price.toFixed(2)}
                      </button>
                      <button data-testid={`buy-pass-${cap.id}`}
                        onClick={(e) => { e.stopPropagation(); handleBuyPass(cap); }} disabled={loading}
                        className="flex-1 bg-[#E5FF00] text-[#080B10] font-semibold uppercase tracking-wider text-[10px] px-3 py-2 hover:bg-[#D4EC00] transition-colors flex items-center justify-center gap-1 disabled:opacity-50 rounded-lg">
                        <Clock size={10} /> {cap.duration_days}-Day Pass ${cap.pass_price.toFixed(2)}
                      </button>
                    </div>
                    <ReviewSection capsuleId={cap.id} user={user} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
